// TAX — passive tribute from citizens. Each owned goon contributes a flat
// m/sec to wealth. Independent of seasons.
//
// Offline catch-up: on init, compute (now − lastTaxTick) × current rate and
// credit it as a single deposit, capped at TaxOfflineCapMs to avoid abuse.

import { Events, emit, on } from '../ui/eventBus.js'
import { GoonTypes, TaxOfflineCapMs, CommercialPlotTribute, PlotTypes, RiceConfig, WorkModes, WorkModeConfig, ClickEconomy, TributeRamp, SchoolConfig, FieldWorkConfig } from '../config/balance.js'
import { stepRamp, tributeRate } from './tributeRamp.js'
import { createRapidClicks } from './fieldWork.js'
import { active as activeKingdom } from './state.js'
import { citizensWorking } from './restManager.js' // runtime-only
import * as season from './seasonManager.js'
import { getFederationBonus } from './kingdomManager.js'
import { getYieldMultiplier } from './eventManager.js'
import { getBonuses as getProfileBonuses, getTitleMultipliers } from './profileManager.js'
import * as idle from './idleManager.js'
import { IdleConfig } from '../config/balance.js'
import { countByType } from './plotState.js'

let acc = 0
let lastFrame = 0
let rafHandle = null
let ramp = 0 // 0…1: how far the rules-based income has ramped in (rapid clicking raises it; see game/tributeRamp.js)
const clicking = createRapidClicks(FieldWorkConfig)
on('paw:click', () => clicking.click(performance.now())) // the player's own presses on the clicker
export const getRamp = () => ramp

// The tribute rate now: the flat idle trickle plus the rules-based income × the ramp. `rampOverride` asks for a given ramp
// (0 = idle only — what an offline stretch earns).
export function getRate(rampOverride = null) {
  // Paused (player stepped away) → no tribute accrues. Pairs with idleManager
  // suspending the Emperor's fines, so pausing is a clean freeze, not a free ride.
  if (idle.isPaused()) return 0
  const fields = season.getWorkMode() === WorkModes.FIELDS
  let rate = 0
  // CR-3 idle wages — peasant 1/s, scribe 2/s (half the per-click contribution),
  // reduced while guarding the village. Injured citizens earn 0 until healed
  // (injuries hit peasants first, then scribes — mirrors getClickValue).
  const P = season.getGoonsOwned(GoonTypes.PEASANT_CAT) - season.getHospitalizedCount(GoonTypes.PEASANT_CAT) // (patients earn nothing)
  const S = season.getGoonsOwned(GoonTypes.SCRIBE_CAT) - season.getHospitalizedCount(GoonTypes.SCRIBE_CAT)
  const injured = season.getInjuredCount()
  const effP = Math.max(0, P - injured)
  const effS = Math.max(0, S - Math.max(0, injured - P))
  // CR-4: the player's tax rate scales the tribute collected from citizens.
  // Citizens asleep (REST) earn no wages; the rice paddies and commercial plots keep paying below.
  const wages = citizensWorking() ? (effP * ClickEconomy.peasantIdlePerSec + effS * ClickEconomy.scribeIdlePerSec) * (1 + season.getTaxRate()) : 0
  rate += fields ? wages : wages * WorkModeConfig.villageWageMult
  // Schooled citizens earn more (FIELDS mode only), up to the number of citizens working.
  if (fields && citizensWorking()) rate += Math.min(activeKingdom().educatedCount ?? 0, effP + effS) * SchoolConfig.tributeBonusEach
  // Commercial plots add flat tribute on top — unaffected by work mode.
  rate += countByType(PlotTypes.COMMERCIAL) * CommercialPlotTribute
  // Rice paddies trickle passive income — untended fields (village mode) less.
  const rice = countByType(PlotTypes.FARM) * RiceConfig.passivePerTile
  rate += fields ? rice : rice * WorkModeConfig.villageRiceMult
  // While the Emperor's fining you, tribute is intercepted — you get 0× of
  // the normal rate. Keeps the AFK exploit math unprofitable.
  const idleMult = idle.isFined() ? IdleConfig.finedTaxMult : 1
  return (
    tributeRate(TributeRamp.idlePerSec, rate, rampOverride ?? ramp) *
    getFederationBonus() *
    getYieldMultiplier() *
    (1 + (getProfileBonuses().taxBonus ?? 0)) *
    getTitleMultipliers().taxMult *
    idleMult
  )
}

export function init() {
  catchupOffline()
  // On kingdom switch the active state slice changes — apply tribute that
  // accrued while this kingdom was paused.
  on(Events.KingdomChange, () => {
    acc = 0 // Wei's fractional carry shouldn't follow you to Shu.
    ramp = 0
    catchupOffline()
  })
  lastFrame = performance.now()
  loop()
}

function catchupOffline() {
  const last = season.getLastTaxTick()
  if (!last) {
    season.setLastTaxTick(Date.now())
    return
  }
  const elapsedMs = Math.min(Date.now() - last, TaxOfflineCapMs)
  if (elapsedMs <= 0) return
  const offlineWealth = Math.floor((getRate(0) * elapsedMs) / 1000) // (away: the idle trickle only — the ramp is earned by clicking)
  if (offlineWealth > 0) {
    season.earnWealth(offlineWealth)
    emit(Events.TaxOfflineEarned, {
      amount: offlineWealth,
      seconds: Math.floor(elapsedMs / 1000),
    })
  }
  season.setLastTaxTick(Date.now())
}

function loop() {
  rafHandle = requestAnimationFrame(loop)
  const now = performance.now()
  const dt = (now - lastFrame) / 1000
  lastFrame = now
  ramp = stepRamp(ramp, clicking.rapid(now), dt * 1000, TributeRamp)
  acc += getRate() * dt
  if (acc >= 1) {
    const whole = Math.floor(acc)
    acc -= whole
    season.earnWealth(whole, { production: true }) // (tax tribute: counts as earning, so a guest's coffer gets it)
    season.setLastTaxTick(Date.now())
  }
}

export function stop() {
  if (rafHandle) cancelAnimationFrame(rafHandle)
  rafHandle = null
}

if (typeof window !== 'undefined' && import.meta.env?.DEV) window.__tribute = { rate: (r) => getRate(r ?? null), ramp: () => ramp, setRamp: (v) => { ramp = v } }
